import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { ReceiptOutlined, Warning } from '@mui/icons-material'
import React from 'react'
import ReactPaginate from 'react-paginate'
import { pagedList } from 'src/viewModels/pagedList'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { ClaimsAcqLogDTO } from 'src/viewModels/ClaimsAcqLogDTO'
import ClaimsAcquisitionNameConstants from 'src/constants/ClaimsAcquisitionNameConstants'
import { inject } from 'mobx-react'
import { IStores } from 'src/Stores'
import { GetClaimsAcquisitionLogByScheduleId } from 'src/services/ClaimsAcquisitionService'
import { ClaimsAcquisitionLogSearchRequest } from 'src/viewModels/ClaimsAcquisitionLogSearchRequest'
import { ClaimsAcquisitionSummaryResult } from 'src/viewModels/ClaimsAcquisitionSummaryResult'
import moment from 'moment'

interface IViewClaimsAcqLogsModalProps {
  isOpen?: boolean
  scheduleId?: string
  closeModal?: () => void
  acq?: ClaimsAcquisitionSummaryResult | undefined
}

class ViewClaimsAcqLogsModal extends React.Component<IViewClaimsAcqLogsModalProps> {
  public async componentDidMount(): Promise<void> {
    await this.getAppealLogData()
  }

  private setPaginationOffsetData = () => {
    const { page, totalItems, pageSize } = this.state
    const firstRecordIndex = (page - 1) * pageSize + 1
    const lastRecordIndex = page * pageSize < totalItems ? page * pageSize : totalItems
    this.setState({
      firstRecordIndex: firstRecordIndex,
      lastRecordIndex: lastRecordIndex,
    })
  }

  private getAppealLogData = async () => {
    const { page, pageSize } = this.state
    const scheduleId = this.props.scheduleId!
    const data: ClaimsAcquisitionLogSearchRequest = {
      scheduleId,
      page,
      pageSize,
    }

    await GetClaimsAcquisitionLogByScheduleId(data).then(async (response: any) => {
      this.setState(
        {
          gridData: response.items,
          pageCount: response.totalPages,
          totalItems: response.totalItems,
        },
        () => {
          if (response.totalItems > 0) {
            this.setPaginationOffsetData()
          }
        }
      )
    })
  }

  private handlePageChange = (event: any) => {
    const page = event.selected
    this.setState({ page: page + 1 }, () => this.getAppealLogData())
  }

  private isGridDataEmpty = () => {
    return this.state.totalItems === 0
  }

  private mapLogGridData = () => {
    if (this.isGridDataEmpty()) {
      return (
        <TableRow className="gridNoPadding" key={1}>
          <TableCell colSpan={10} align="center">
            No records found
          </TableCell>
        </TableRow>
      )
    } else {
      return this.state.gridData.map((record, index) => (
        <TableRow className="gridPadding" key={index}>
          <TableCell>
            {record.urgent && (
              <Tooltip title="Urgent">
                <Warning color="error" fontSize="small" />
              </Tooltip>
            )}
          </TableCell>
          <TableCell>
            {record.createdDate ? moment(record.createdDate).format('MM/DD/YYYY') : ''}
          </TableCell>
          <TableCell>{record.createdByName}</TableCell>
          <TableCell>{ClaimsAcquisitionNameConstants[record.claimsAcqStatus]}</TableCell>
          <TableCell>
            {record.followUpDate ? moment(record.followUpDate).format('MM/DD/YYYY') : ''}
          </TableCell>
          <TableCell>{record.notes}</TableCell>
        </TableRow>
      ))
    }
  }

  private paginationInfo = () => {
    if (!this.isGridDataEmpty()) {
      return (
        <div className="pagination">
          <Typography variant="caption" gutterBottom>
            Showing {this.state.firstRecordIndex} to {this.state.lastRecordIndex} of{' '}
            {this.state.totalItems} entries
          </Typography>
        </div>
      )
    } else {
      return <></>
    }
  }

  public state = {
    gridData: new pagedList().items as ClaimsAcqLogDTO[],
    page: 1,
    pageCount: 1,
    firstRecordIndex: 0,
    lastRecordIndex: 0,
    totalItems: 0,
    pageSize: 5,
    claimId: this.props.scheduleId,
  }

  public render() {
    const { acq } = this.props
    const patientName = acq?.patientName
    const dob =
      acq?.dateOfBirth != undefined ? moment(acq?.dateOfBirth).format('MM/DD/YYYY') : ''
    const eoc = acq?.eocLegacyNum
    const group = acq?.providerGroupName
    const component = acq?.componentName
    const dos =
      acq?.dateOfService != undefined
        ? moment(acq?.dateOfService).format('MM/DD/YYYY')
        : ''
    var modalTitle = 'Claims Acquisition Log - ' + patientName + ' (' + dob + ') ' + eoc
    return (
      <>
        <Dialog
          key={this.props.scheduleId}
          fullWidth={true}
          maxWidth="lg"
          open={this.props.isOpen!}
          onClose={(_, reason) => {
            if (reason !== 'backdropClick') {
              this.props.closeModal?.()
            }
          }}
        >
          <DialogTitle style={{ padding: 0 }}>
            <ResponsiveAppBar
              title={modalTitle}
              pageIcon={<ReceiptOutlined />}
              isModal={true}
            />
          </DialogTitle>
          <DialogContent style={{ padding: '12px 12px 8px' }}>
            <Grid
              container
              item
              xs={12}
              spacing={2}
              style={{ paddingBottom: '10px' }}
              wrap="nowrap"
            >
              <Grid item>
                <strong>Component: </strong>
                {component!}
              </Grid>
              <Grid item>
                <strong>Provider Group: </strong> {group}
              </Grid>
              <Grid item>
                <strong>Date of Service: </strong> {dos}
              </Grid>
            </Grid>
            <Grid
              item
              container
              direction="row"
              justifyContent="space-between"
              alignContent="center"
              alignItems="flex-start"
              wrap="wrap"
            >
              <Grid item xs={12} style={{ minWidth: '750px' }}>
                <Table aria-label="simple table">
                  <TableHead>
                    <TableRow className="gridPadding">
                      <TableCell></TableCell>
                      <TableCell>Created Date</TableCell>
                      <TableCell>Created By</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Follow-Up Date</TableCell>
                      <TableCell>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody style={{ width: '100%' }}>{this.mapLogGridData()}</TableBody>
                </Table>
                <div className="pagination-row">
                  {this.paginationInfo()}
                  <ReactPaginate
                    previousLabel={'<'}
                    nextLabel={'>'}
                    onPageChange={this.handlePageChange}
                    forcePage={this.state.page - 1}
                    pageCount={this.state.pageCount}
                    containerClassName={'pagination'}
                    activeClassName={'active'}
                    //@ts-ignore
                    renderOnZeroPageCount={null}
                  />
                </div>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.props.closeModal} data-cy="close">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </>
    )
  }
}

const InjectedViewClaimsAcqLogsModal = inject<
  IStores,
  IViewClaimsAcqLogsModalProps,
  Partial<IViewClaimsAcqLogsModalProps>,
  any
>((stores: IStores) => ({
  isOpen: stores.schedules.isViewClaimsAcqLogModalOpen,
  closeModal: stores.schedules.closeViewClaimsAcqLogModal,
  acq: stores.schedules.selectedClaimsAcquisition,
  scheduleId: stores.schedules.selectedScheduleId,
}))(ViewClaimsAcqLogsModal)

export default InjectedViewClaimsAcqLogsModal
